import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useCanEdit } from "@/lib/permissions";
import { useConfirm } from "@/context/ConfirmContext";
import { imageSrc } from "@/pages/Products";
import { PROJECT_STATUS_META, fmtMoney } from "@/pages/Projects";
import {
  ArrowLeft, Check, Plus, Trash2, FileText, ImagePlus, StickyNote, Wallet, CalendarDays,
  ChevronUp, ChevronDown, FileBarChart, AlertTriangle, History,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CURRENCIES = ["AUD", "USD", "EUR", "GBP", "NZD", "NOK", "SGD"];
const SEVERITY_META = {
  low: "bg-[#E8EEF3] text-[#5B6B7C]",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
};
const fmtD = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const confirm = useConfirm();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newMilestone, setNewMilestone] = useState("");
  const [newNote, setNewNote] = useState("");
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);
  const [clients, setClients] = useState([]);
  const [newIssue, setNewIssue] = useState("");
  const [newIssueSeverity, setNewIssueSeverity] = useState("medium");
  const photoRef = useRef(null);
  const docRef = useRef(null);
  const reportRef = useRef(null);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get(`/projects/${id}`)
      .then(({ data }) => setP(data))
      .catch(() => toast.error("Could not load project"));
  }, [id]);

  const save = async (patch, quiet = false) => {
    const next = { ...p, ...patch };
    setP(next);
    setSaving(true);
    try {
      const body = {
        name: next.name,
        client_id: next.client_id || null,
        vessel_id: next.vessel_id || null,
        status: next.status,
        start_date: next.start_date || null,
        delivery_date: next.delivery_date || null,
        next_report_date: next.next_report_date || null,
        percent_complete: next.percent_complete,
        milestones: next.milestones || [],
        notes: next.notes || [],
        photos: next.photos || [],
        documents: next.documents || [],
        reports: next.reports || [],
        issues: next.issues || [],
        delivery_history: next.delivery_history || [],
        contract_value: next.contract_value != null && next.contract_value !== "" ? Number(next.contract_value) : null,
        currency: next.currency || "AUD",
        costs_to_date: next.costs_to_date != null && next.costs_to_date !== "" ? Number(next.costs_to_date) : null,
      };
      const { data } = await api.put(`/projects/${id}`, body);
      setP(data);
      if (!quiet) toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleMilestone = (mid) =>
    save(
      {
        milestones: p.milestones.map((m) =>
          m.id === mid
            ? { ...m, done: !m.done, date: !m.done ? new Date().toISOString().slice(0, 10) : null }
            : m
        ),
      },
      true
    );

  const addMilestone = () => {
    if (!newMilestone.trim()) return;
    save(
      {
        milestones: [
          ...(p.milestones || []),
          { id: `m${Date.now()}`, label: newMilestone.trim(), done: false, date: null },
        ],
      },
      true
    );
    setNewMilestone("");
  };

  const moveMilestone = (idx, dir) => {
    const ms = [...p.milestones];
    const j = idx + dir;
    if (j < 0 || j >= ms.length) return;
    [ms[idx], ms[j]] = [ms[j], ms[idx]];
    save({ milestones: ms }, true);
  };

  const onReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReport(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${id}/reports`, fd);
      setP(data.project);
      if (data.delivery_date_changed) {
        toast.success(`Delivery date updated to ${fmtD(data.extracted_delivery_date)} — per "${file.name}"`, { duration: 9000 });
      } else if (data.extracted_delivery_date) {
        toast.success("Report attached — delivery date unchanged");
      } else {
        toast.success("Report attached — no delivery date found in it");
      }
      if (data.issues_added) {
        toast.info(`${data.issues_added} issue${data.issues_added > 1 ? "s" : ""} pulled into the issues register`, { duration: 9000 });
      }
      if (data.photos_added) {
        toast.info(`${data.photos_added} build photo${data.photos_added > 1 ? "s" : ""} extracted into the gallery`, { duration: 9000 });
      }
      if (data.next_report_date) {
        toast.info(`Next report scheduled for ${fmtD(data.next_report_date)}`, { duration: 9000 });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Report upload failed");
    } finally {
      setUploadingReport(false);
      if (reportRef.current) reportRef.current.value = "";
    }
  };

  const addIssue = () => {
    if (!newIssue.trim()) return;
    save(
      {
        issues: [
          {
            id: `i${Date.now()}`,
            title: newIssue.trim(),
            detail: "",
            severity: newIssueSeverity,
            status: "open",
            source: "Manual",
            created_at: new Date().toISOString(),
          },
          ...(p.issues || []),
        ],
      },
      true
    );
    setNewIssue("");
  };

  const addNote = () => {
    if (!newNote.trim()) return;
    save(
      {
        notes: [
          { id: `n${Date.now()}`, text: newNote.trim(), author: user?.name || "", created_at: new Date().toISOString() },
          ...(p.notes || []),
        ],
      },
      true
    );
    setNewNote("");
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhotos(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        added.push({ path: data.path, caption: "", uploaded_at: new Date().toISOString() });
      } catch {
        toast.error(`${file.name} failed to upload`);
      }
    }
    if (added.length) await save({ photos: [...(p.photos || []), ...added] }, true);
    setUploadingPhotos(false);
    if (photoRef.current) photoRef.current.value = "";
    if (added.length) toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} added`);
  };

  const onDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      await save(
        { documents: [...(p.documents || []), { title: file.name, path: data.path, uploaded_at: new Date().toISOString() }] },
        true
      );
      toast.success("Document attached");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingDoc(false);
      if (docRef.current) docRef.current.value = "";
    }
  };

  const removeProject = async () => {
    const ok = await confirm({
      title: "Delete this project?",
      description: "This removes the project, its notes, photo references and document links.",
      confirmText: "Delete",
    });
    if (!ok) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    navigate("/projects");
  };

  if (!p) return <div className="p-8 text-sm text-[#5B6B7C]">Loading…</div>;

  const meta = PROJECT_STATUS_META[p.status] || PROJECT_STATUS_META.design;
  const remaining = p.contract_value != null && p.costs_to_date != null ? p.contract_value - p.costs_to_date : null;

  return (
    <div className="p-6 lg:p-8 max-w-[1200px]" data-testid="project-detail-page">
      <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-[#0088CC] hover:underline mb-4" data-testid="back-to-projects">
        <ArrowLeft className="h-4 w-4" /> All projects
      </Link>

      {/* Header */}
      <div className="sos-surface rounded-lg p-5 mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold text-[#011836]" data-testid="project-title">{p.name}</h1>
            <div className="text-sm text-[#5B6B7C] mt-1 flex items-center gap-1.5 flex-wrap">
              {canEdit ? (
                <Select value={p.client_id || "none"} onValueChange={(v) => save({ client_id: v === "none" ? null : v }, true)}>
                  <SelectTrigger className="w-52 bg-white border-[#DDE5EC] h-8 text-xs" data-testid="project-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectItem value="none">— No client —</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span>{p.client_name || "No client"}</span>
              )}
              {p.vessel_name && (
                <>
                  {" · "}
                  <Link to={`/vessels/${p.vessel_id}`} className="text-[#0088CC] hover:underline">{p.vessel_name}</Link>
                </>
              )}
              {p.quote_id && (
                <>
                  {" · "}
                  <Link to={`/quotes/${p.quote_id}`} className="text-[#0088CC] hover:underline">View quote</Link>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit ? (
              <Select value={p.status} onValueChange={(v) => save({ status: v }, true)}>
                <SelectTrigger className="w-36 bg-white border-[#DDE5EC] h-9" data-testid="project-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                  {Object.entries(PROJECT_STATUS_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${meta.className}`}>{meta.label}</span>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" onClick={removeProject} className="border-[#FCA5A5] bg-white text-[#EF4444] hover:bg-red-50 h-9" data-testid="project-delete-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="mt-5">
          <div className="flex justify-between text-xs text-[#5B6B7C] mb-1.5">
            <span className="uppercase tracking-wider font-semibold">Progress</span>
            <span className="font-bold text-[#011836]" data-testid="project-progress-value">{p.progress}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-[#E8EEF3] overflow-hidden">
            <div className="h-full rounded-full bg-[#0088CC] transition-all" style={{ width: `${p.progress}%` }} />
          </div>
        </div>

        {/* Dates + financials */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-5">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Start</Label>
            <Input type="date" disabled={!canEdit} value={p.start_date || ""} onChange={(e) => save({ start_date: e.target.value }, true)} className="mt-1 bg-white border-[#DDE5EC] h-9" data-testid="project-start-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Delivery</Label>
            <Input type="date" disabled={!canEdit} value={p.delivery_date || ""} onChange={(e) => save({ delivery_date: e.target.value }, true)} className="mt-1 bg-white border-[#DDE5EC] h-9" data-testid="project-delivery-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1"><FileBarChart className="h-3 w-3" /> Next report</Label>
            <Input type="date" disabled={!canEdit} value={p.next_report_date || ""} onChange={(e) => save({ next_report_date: e.target.value }, true)} className="mt-1 bg-white border-[#DDE5EC] h-9" data-testid="project-next-report-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1"><Wallet className="h-3 w-3" /> Contract value</Label>
            <div className="flex gap-1.5 mt-1">
              <Input type="number" disabled={!canEdit} value={p.contract_value ?? ""} onBlur={(e) => save({ contract_value: e.target.value }, true)} onChange={(e) => setP({ ...p, contract_value: e.target.value })} className="bg-white border-[#DDE5EC] h-9 tabular-nums" data-testid="project-contract-value" />
              <Select value={p.currency || "AUD"} onValueChange={(v) => save({ currency: v }, true)} disabled={!canEdit}>
                <SelectTrigger className="w-20 bg-white border-[#DDE5EC] h-9 px-2 text-xs" data-testid="project-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1"><Wallet className="h-3 w-3" /> Costs to date</Label>
            <Input type="number" disabled={!canEdit} value={p.costs_to_date ?? ""} onBlur={(e) => save({ costs_to_date: e.target.value }, true)} onChange={(e) => setP({ ...p, costs_to_date: e.target.value })} className="mt-1 bg-white border-[#DDE5EC] h-9 tabular-nums" data-testid="project-costs" />
          </div>
        </div>
        {remaining != null && (
          <div className="mt-2 text-xs text-[#5B6B7C]" data-testid="project-remaining">
            Remaining budget: <span className={`font-bold ${remaining < 0 ? "text-[#EF4444]" : "text-[#011836]"}`}>{fmtMoney(remaining, p.currency)}</span>
          </div>
        )}
        {(p.delivery_history || []).length > 0 && (
          <div className="mt-3 rounded-md border border-[#E8EEF3] bg-[#F8FAFC] px-3.5 py-2.5" data-testid="delivery-history">
            <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] font-semibold flex items-center gap-1.5 mb-1.5">
              <History className="h-3 w-3" /> Delivery date history
            </div>
            <div className="space-y-1">
              {p.delivery_history.map((h, i) => (
                <div key={i} className="text-xs text-[#334155]" data-testid={`delivery-history-${i}`}>
                  <span className="font-semibold text-[#011836]">{fmtD(h.date)}</span>
                  {h.previous && <span className="text-[#94A3B8]"> (was {fmtD(h.previous)})</span>}
                  {" · per "}
                  {h.report_path ? (
                    <a href={`${API}/files/${h.report_path}?auth=${encodeURIComponent(localStorage.getItem("sos_access_token") || "")}`} target="_blank" rel="noreferrer" className="text-[#0088CC] hover:underline">
                      {h.source || "report"}
                    </a>
                  ) : (
                    <span>{h.source || "manual change"}</span>
                  )}
                  <span className="text-[#94A3B8]"> · {fmtD(h.changed_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Milestones */}
        <div className="sos-surface rounded-lg" data-testid="project-milestones">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center gap-2">
            <Check className="h-4 w-4 text-[#0088CC]" />
            <h2 className="font-display text-lg font-semibold text-[#011836]">Milestones</h2>
          </div>
          <div className="p-4 space-y-1.5">
            {(p.milestones || []).map((m, mi) => (
              <div key={m.id} className="flex items-center gap-2.5 rounded-md border border-[#E8EEF3] bg-white px-3 py-2.5" data-testid={`milestone-${m.id}`}>
                <input type="checkbox" checked={!!m.done} disabled={!canEdit} onChange={() => toggleMilestone(m.id)} className="h-4 w-4 accent-[#0088CC] cursor-pointer" data-testid={`milestone-toggle-${m.id}`} />
                <span className={`text-sm flex-1 ${m.done ? "text-[#94A3B8] line-through" : "text-[#011836]"}`}>{m.label}</span>
                {m.done && m.date && <span className="text-[10px] text-[#5B6B7C]">{new Date(m.date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>}
                {canEdit && (
                  <span className="flex items-center gap-0.5">
                    <button onClick={() => moveMilestone(mi, -1)} disabled={mi === 0} className="text-[#94A3B8] hover:text-[#0088CC] disabled:opacity-25" aria-label="Move up" data-testid={`milestone-up-${m.id}`}>
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button onClick={() => moveMilestone(mi, 1)} disabled={mi === p.milestones.length - 1} className="text-[#94A3B8] hover:text-[#0088CC] disabled:opacity-25" aria-label="Move down" data-testid={`milestone-down-${m.id}`}>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    <button onClick={() => save({ milestones: p.milestones.filter((x) => x.id !== m.id) }, true)} className="text-[#94A3B8] hover:text-[#EF4444] ml-1" aria-label="Remove milestone">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                )}
              </div>
            ))}
            {canEdit && (
              <div className="flex gap-2 pt-2">
                <Input value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMilestone()} placeholder="Add a milestone…" className="bg-white border-[#DDE5EC] h-9" data-testid="new-milestone-input" />
                <Button size="sm" onClick={addMilestone} className="bg-[#0088CC] hover:bg-[#006DA6] text-white h-9" data-testid="add-milestone-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="sos-surface rounded-lg" data-testid="project-notes">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-[#0088CC]" />
            <h2 className="font-display text-lg font-semibold text-[#011836]">Progress notes</h2>
          </div>
          <div className="p-4 space-y-3">
            {canEdit && (
              <div className="flex flex-col gap-2">
                <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="What happened this week?" rows={2} className="bg-white border-[#DDE5EC]" data-testid="new-note-input" />
                <Button size="sm" onClick={addNote} className="bg-[#0088CC] hover:bg-[#006DA6] text-white self-end" data-testid="add-note-btn">Add note</Button>
              </div>
            )}
            {(p.notes || []).length === 0 && <div className="text-sm text-[#94A3B8]">No notes yet.</div>}
            {(p.notes || []).map((n) => (
              <div key={n.id} className="rounded-md border border-[#E8EEF3] bg-white px-3.5 py-3" data-testid={`note-${n.id}`}>
                <div className="text-sm text-[#011836] whitespace-pre-line">{n.text}</div>
                <div className="text-[10px] text-[#5B6B7C] mt-1.5 flex items-center justify-between">
                  <span>{n.author}</span>
                  <span>{n.created_at ? new Date(n.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reports — AI-scanned for delivery date + issues */}
        <div className="sos-surface rounded-lg" data-testid="project-reports">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileBarChart className="h-4 w-4 text-[#0088CC]" />
              <h2 className="font-display text-lg font-semibold text-[#011836]">Reports</h2>
            </div>
            {canEdit && (
              <>
                <input ref={reportRef} type="file" accept=".pdf,.docx" className="hidden" onChange={onReport} data-testid="project-report-input" />
                <Button size="sm" variant="outline" disabled={uploadingReport} onClick={() => reportRef.current?.click()} className="border-[#9CCFEA] bg-white text-[#0088CC] hover:bg-[#E6F2FA] h-8 text-xs" data-testid="add-report-btn">
                  <Plus className="h-3 w-3 mr-1" /> {uploadingReport ? "Analysing report…" : "Upload report"}
                </Button>
              </>
            )}
          </div>
          <div className="p-4 space-y-1.5">
            <p className="text-[11px] text-[#94A3B8] mb-2">
              Reports are scanned automatically — a revised delivery date updates the project (with history), and raised issues land in the issues register.
            </p>
            {(p.reports || []).length === 0 && <div className="text-sm text-[#94A3B8]">No reports yet.</div>}
            {[...(p.reports || [])].reverse().map((r) => (
              <div key={r.id} className="rounded-md border border-[#E8EEF3] bg-white px-3 py-2.5" data-testid={`project-report-${r.id}`}>
                <div className="flex items-center gap-2">
                  <FileBarChart className="h-4 w-4 text-[#0088CC] shrink-0" />
                  <a href={`${API}/files/${r.path}?auth=${encodeURIComponent(localStorage.getItem("sos_access_token") || "")}`} target="_blank" rel="noreferrer" className="text-sm text-[#011836] hover:text-[#0088CC] truncate flex-1">
                    {r.title}
                  </a>
                  <span className="text-[10px] text-[#5B6B7C]">{fmtD(r.uploaded_at)}</span>
                  {canEdit && (
                    <button onClick={() => save({ reports: p.reports.filter((x) => x.id !== r.id) }, true)} className="text-[#94A3B8] hover:text-[#EF4444]" aria-label="Remove report">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {(r.extracted_delivery_date || r.issues_added > 0 || r.photos_added > 0) && (
                  <div className="text-[11px] text-[#5B6B7C] mt-1 ml-6">
                    {[
                      r.extracted_delivery_date ? `Delivery date found: ${fmtD(r.extracted_delivery_date)}` : null,
                      r.issues_added > 0 ? `${r.issues_added} issue${r.issues_added > 1 ? "s" : ""} extracted` : null,
                      r.photos_added > 0 ? `${r.photos_added} photo${r.photos_added > 1 ? "s" : ""} extracted` : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Issues register */}
        <div className="sos-surface rounded-lg" data-testid="project-issues">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#F59E0B]" />
            <h2 className="font-display text-lg font-semibold text-[#011836]">Issues register</h2>
            {(p.issues || []).filter((i) => i.status === "open").length > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {(p.issues || []).filter((i) => i.status === "open").length} open
              </span>
            )}
          </div>
          <div className="p-4 space-y-1.5">
            {canEdit && (
              <div className="flex gap-2 mb-2">
                <Input value={newIssue} onChange={(e) => setNewIssue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addIssue()} placeholder="Log an issue…" className="bg-white border-[#DDE5EC] h-9" data-testid="new-issue-input" />
                <Select value={newIssueSeverity} onValueChange={setNewIssueSeverity}>
                  <SelectTrigger className="w-28 bg-white border-[#DDE5EC] h-9 text-xs" data-testid="new-issue-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={addIssue} className="bg-[#0088CC] hover:bg-[#006DA6] text-white h-9" data-testid="add-issue-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            )}
            {(p.issues || []).length === 0 && <div className="text-sm text-[#94A3B8]">No issues logged — clean build so far.</div>}
            {(p.issues || []).map((i) => (
              <div key={i.id} className={`rounded-md border px-3 py-2.5 ${i.status === "closed" ? "border-[#E8EEF3] bg-[#F8FAFC] opacity-70" : "border-[#E8EEF3] bg-white"}`} data-testid={`issue-${i.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${SEVERITY_META[i.severity] || SEVERITY_META.medium}`}>{i.severity}</span>
                  <span className={`text-sm flex-1 ${i.status === "closed" ? "line-through text-[#94A3B8]" : "text-[#011836]"}`}>{i.title}</span>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => save({ issues: p.issues.map((x) => (x.id === i.id ? { ...x, status: x.status === "open" ? "closed" : "open" } : x)) }, true)}
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${i.status === "open" ? "border-emerald-300 text-emerald-600 hover:bg-emerald-50" : "border-[#CBD5E1] text-[#5B6B7C] hover:bg-[#F1F5F9]"}`}
                        data-testid={`issue-toggle-${i.id}`}
                      >
                        {i.status === "open" ? "Close" : "Reopen"}
                      </button>
                      <button onClick={() => save({ issues: p.issues.filter((x) => x.id !== i.id) }, true)} className="text-[#94A3B8] hover:text-[#EF4444]" aria-label="Delete issue">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
                {i.detail && <div className="text-xs text-[#5B6B7C] mt-1 ml-1">{i.detail}</div>}
                <div className="text-[10px] text-[#94A3B8] mt-1 ml-1">
                  {i.source ? `Source: ${i.source} · ` : ""}{fmtD(i.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Photos */}
        <div className="sos-surface rounded-lg" data-testid="project-photos">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ImagePlus className="h-4 w-4 text-[#0088CC]" />
              <h2 className="font-display text-lg font-semibold text-[#011836]">Build photos</h2>
            </div>
            {canEdit && (
              <>
                <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid="project-photo-input" />
                <Button size="sm" variant="outline" disabled={uploadingPhotos} onClick={() => photoRef.current?.click()} className="border-[#9CCFEA] bg-white text-[#0088CC] hover:bg-[#E6F2FA] h-8 text-xs" data-testid="add-photos-btn">
                  <Plus className="h-3 w-3 mr-1" /> {uploadingPhotos ? "Uploading…" : "Add photos"}
                </Button>
              </>
            )}
          </div>
          <div className="p-4">
            {(p.photos || []).length === 0 ? (
              <div className="text-sm text-[#94A3B8]">No photos yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {p.photos.map((ph, i) => (
                  <div key={i} className="relative group rounded-md overflow-hidden border border-[#E8EEF3] bg-[#F6F7F8]" data-testid={`project-photo-${i}`}>
                    <img src={imageSrc(ph.path)} alt={ph.caption || `Build photo ${i + 1}`} className="w-full h-28 object-cover" />
                    {canEdit && (
                      <button onClick={() => save({ photos: p.photos.filter((_, j) => j !== i) }, true)} className="absolute top-1.5 right-1.5 bg-white/90 rounded p-1 text-[#EF4444] opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove photo">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Documents */}
        <div className="sos-surface rounded-lg" data-testid="project-documents">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#0088CC]" />
              <h2 className="font-display text-lg font-semibold text-[#011836]">Documents</h2>
            </div>
            {canEdit && (
              <>
                <input ref={docRef} type="file" className="hidden" onChange={onDoc} data-testid="project-doc-input" />
                <Button size="sm" variant="outline" disabled={uploadingDoc} onClick={() => docRef.current?.click()} className="border-[#9CCFEA] bg-white text-[#0088CC] hover:bg-[#E6F2FA] h-8 text-xs" data-testid="add-doc-btn">
                  <Plus className="h-3 w-3 mr-1" /> {uploadingDoc ? "Uploading…" : "Attach document"}
                </Button>
              </>
            )}
          </div>
          <div className="p-4 space-y-1.5">
            {(p.documents || []).length === 0 && <div className="text-sm text-[#94A3B8]">No documents yet.</div>}
            {(p.documents || []).map((d, i) => (
              <div key={i} className="flex items-center gap-2 rounded-md border border-[#E8EEF3] bg-white px-3 py-2.5" data-testid={`project-doc-${i}`}>
                <FileText className="h-4 w-4 text-[#0088CC] shrink-0" />
                <a href={`${API}/files/${d.path}?auth=${encodeURIComponent(localStorage.getItem("sos_access_token") || "")}`} target="_blank" rel="noreferrer" className="text-sm text-[#011836] hover:text-[#0088CC] truncate flex-1">
                  {d.title || d.path.split("/").pop()}
                </a>
                <span className="text-[10px] text-[#5B6B7C]">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : ""}</span>
                {canEdit && (
                  <button onClick={() => save({ documents: p.documents.filter((_, j) => j !== i) }, true)} className="text-[#94A3B8] hover:text-[#EF4444]" aria-label="Remove document">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {saving && <div className="fixed bottom-4 right-4 text-xs text-[#5B6B7C] bg-white border border-[#DDE5EC] rounded px-2 py-1">Saving…</div>}
    </div>
  );
}
